import React, { useState, useEffect } from 'react';
import { X, Flame } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { trackEvent } from '@/lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CAMPAIGN_ID = 'welcome_banner';

const DEFAULTS = {
  is_active: true,
  display_type: 'image_text',
  ribbon_tr: 'GÜNÜN ÜRÜNÜ',
  ribbon_en: 'PRODUCT OF THE DAY',
  title_tr: 'PEYNİRLİ PİDE + AYRAN',
  title_en: 'CHEESE PIDE + AYRAN',
  subtitle_tr: 'Eşsiz İkili',
  subtitle_en: 'Perfect Duo',
  description_tr: 'Sıcacık peynirli pide yanında soğuk ayranla, lezzet dolu bir mola sizi bekliyor!',
  description_en: 'Hot cheese pide served with cold ayran — a tasty break is waiting for you!',
  old_price: '₺650',
  new_price: '₺600',
  image: '/images/menu/placeholder.webp',
  limited_tr: 'Sınırlı süre!',
  limited_en: 'Limited time!',
};

// Classify an image by its natural dimensions
const classifyRatio = (w, h) => {
  if (!w || !h) return { shape: 'landscape', ratio: 16 / 9 };
  const ratio = w / h;
  if (ratio > 1.1) return { shape: 'landscape', ratio };
  if (ratio < 0.9) return { shape: 'portrait', ratio };
  return { shape: 'square', ratio };
};

// Shape → popup max-width (responsive via tailwind-friendly px values)
const widthForShape = (shape) => {
  if (shape === 'portrait') return 'max-w-xs';   // ~320px — narrow + tall
  if (shape === 'square') return 'max-w-sm';     // ~384px
  return 'max-w-lg';                             // landscape — ~512px
};

const CampaignPopup = ({ onClose }) => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [data, setData] = useState(DEFAULTS);
  const [imgInfo, setImgInfo] = useState({ shape: 'landscape', ratio: 16 / 9 });

  useEffect(() => {
    fetch(`${API_URL}/api/campaign`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.is_active !== false) {
          setData({ ...DEFAULTS, ...d });
        } else if (d && d.is_active === false) {
          onClose?.();
        }
      })
      .catch(() => { /* keep defaults */ });

    const t = setTimeout(() => setIsVisible(true), 100);
    trackEvent('campaign_view', { campaign_id: CAMPAIGN_ID });
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = {
    ribbon: language === 'en' ? data.ribbon_en : data.ribbon_tr,
    title: language === 'en' ? data.title_en : data.title_tr,
    subtitle: language === 'en' ? data.subtitle_en : data.subtitle_tr,
    description: language === 'en' ? data.description_en : data.description_tr,
    oldPrice: data.old_price,
    newPrice: data.new_price,
    limitedTime: language === 'en' ? data.limited_en : data.limited_tr,
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handlePopupClick = (e) => {
    if (e.target.closest('[data-role="campaign-close"]')) return;
    trackEvent('campaign_click', { campaign_id: CAMPAIGN_ID });
  };

  const handleImgLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    setImgInfo(classifyRatio(w, h));
  };

  const campaignImage = data.image || DEFAULTS.image;
  const popupWidthClass = widthForShape(imgInfo.shape);
  // Image container uses aspect-ratio so no cropping; naturally fits inside popup
  const imageContainerStyle = { aspectRatio: imgInfo.ratio };
  const imageOnly = data.display_type === 'image_only';

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      data-testid="campaign-popup"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div
        onClick={handlePopupClick}
        data-shape={imgInfo.shape}
        data-display-type={data.display_type || 'image_text'}
        data-testid="campaign-popup-content"
        className={`relative w-[94vw] ${popupWidthClass} max-h-[92vh] ${imageOnly ? 'bg-transparent border-0 shadow-2xl' : 'bg-gradient-to-b from-card to-background border border-primary/30 shadow-2xl'} rounded-3xl overflow-hidden transition-all duration-300 flex flex-col ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        <button
          onClick={handleClose}
          data-role="campaign-close"
          data-testid="campaign-close-btn"
          aria-label="Kampanyayı kapat"
          className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white hover:bg-black/70 transition-all"
        >
          <X className="w-5 h-5" />
        </button>

        {!imageOnly && (
          <div className="absolute top-3 left-3 z-20">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-bold animate-pulse">
              <Flame className="w-3.5 h-3.5" />
              {t.ribbon}
            </div>
          </div>
        )}

        {/* Scrollable area so popup can never overflow viewport */}
        <div className="overflow-y-auto flex-1 flex flex-col">
          {/* Image container — aspect-ratio preserves the natural ratio, contain prevents crop */}
          <div
            className={`relative w-full ${imageOnly ? 'bg-transparent' : 'bg-charcoal-light'} flex items-center justify-center`}
            style={imageContainerStyle}
          >
            <img
              src={campaignImage}
              alt={t.title || 'Kampanya'}
              onLoad={handleImgLoad}
              className="w-full h-full object-contain"
              style={{ objectPosition: 'center' }}
              onError={(e) => {
                if (!e.currentTarget.src.endsWith('/images/menu/placeholder.webp')) {
                  e.currentTarget.src = '/images/menu/placeholder.webp';
                }
              }}
            />
          </div>

          {!imageOnly && (
            <div className="p-5">
              <h2 className="font-display text-2xl text-foreground mb-1">
                {t.title}
              </h2>
              {t.subtitle && (
                <p className="text-primary text-sm font-medium mb-3">
                  {t.subtitle}
                </p>
              )}

              {t.description && (
                <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                  {t.description}
                </p>
              )}

              <div className="flex items-end gap-4 mb-1">
                {t.oldPrice && (
                  <span className="text-red-500 line-through text-2xl font-medium">
                    {t.oldPrice}
                  </span>
                )}
                {t.newPrice && (
                  <span className="text-primary font-display text-4xl leading-none">
                    {t.newPrice}
                  </span>
                )}
              </div>
              {t.limitedTime && (
                <span className="text-secondary text-xs font-medium px-2 py-1 bg-secondary/20 rounded-full inline-block">
                  {t.limitedTime}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignPopup;
